// Catalog Admin — product detail (2026-05-12 #9).
//
// Per-product editor for the canonical merch'd product. Sections:
//   1. Header — name, brand, category, hide/unhide button.
//   2. Image gallery — preview of canonical merchd_products.imageUrls
//      with a fallback note when the row inherits from the preferred
//      supplier offer.
//   3. Edit form — name, brand, description, category. PATCH saves.
//   4. Image URL editor — paste-in list, save replaces imageUrls.
//   5. Prices (admins) — quantity rows, or capacity × quantity for our USB
//      drives (components/catalog/PriceEditor.tsx, Jake 2026-11-02).
//   6. Supplier offers — list with variant counts, supplier costs, vendor
//      badges. Preferred offer highlighted.
//
// Auth: admin only. The page renders an empty 401 state for non-admins;
// the API gate is the source of truth.

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { StatusPill } from "@/components/ui/StatusPill";
import { DeleteEntityAction } from "@/components/common/DeleteEntityAction";
import { PriceEditorSection } from "@/components/catalog/PriceEditor";
import { useAuth } from "@/auth/AuthProvider";
import { ApiError, api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface AdminCategoryRow {
  id: string;
  slug: string;
  name: string;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
  productCount: number;
}

interface CategoriesResponse {
  rows: AdminCategoryRow[];
}

interface AdminProductOfferDetail {
  offerId: string;
  supplierProductId: string;
  vendorId: string;
  vendorSlug: string | null;
  vendorName: string;
  isPreferred: boolean;
  isActive: boolean;
  lowestBlankPiecePrice: string | null;
  lowestDecoratedPiecePrice: string | null;
  imageUrls: string[];
  description: string | null;
  variantCount: number;
}

interface AdminProductDetail {
  id: string;
  name: string;
  description: string | null;
  brand: string | null;
  sku: string | null;
  category: { id: string; slug: string; name: string } | null;
  imageUrls: string[];
  fallbackImageUrls: string[];
  lowestBlankPiecePrice: string | null;
  lowestDecoratedPiecePrice: string | null;
  costOverrideDecorated: string | null;
  isActive: boolean;
  isHidden: boolean;
  offers: AdminProductOfferDetail[];
  createdAt: string;
  updatedAt: string;
}

interface DetailResponse {
  product: AdminProductDetail;
  /** Made by hand ("Add a custom product") — reps may edit/delete these. */
  custom?: boolean;
}

export function CatalogProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isAdmin = user?.roles.includes("admin") ?? false;
  // Back to the same filtered list the product was opened from (Stephanie 2026-11-02).
  const location = useLocation();
  const fromState = (location.state as { backTo?: unknown } | null)?.backTo;
  const backTo = typeof fromState === "string" && fromState.startsWith("/admin/catalog/products") ? fromState : "/admin/catalog/products";
  // Reps open this page for the products they made by hand (Stephanie
  // 2026-10-30); the server refuses feed products for them.
  const canOpen = isAdmin || (user?.roles.some((r) => r === "production_rep" || r === "client_rep") ?? false);
  const queryClient = useQueryClient();

  const detailKey = useMemo(
    () => ["admin", "catalog", "product", id] as const,
    [id],
  );

  const detailQ = useQuery({
    queryKey: detailKey,
    queryFn: () => api<DetailResponse>(`/admin/catalog/products/${id}`),
    enabled: canOpen && Boolean(id),
  });

  const categoriesQ = useQuery({
    queryKey: ["admin", "categories"] as const,
    queryFn: () => api<CategoriesResponse>("/admin/categories"),
    enabled: isAdmin,
    staleTime: 60_000,
  });

  if (!canOpen) {
    return (
      <div className="mx-auto w-full">
        <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          Catalog
        </h1>
        <p className="mt-3 text-sm text-brown-300">
          This admin surface is only visible to users with the admin role.
        </p>
      </div>
    );
  }

  if (detailQ.isLoading) {
    return (
      <p className="mx-auto w-full text-sm text-brown-300">Loading…</p>
    );
  }
  if (detailQ.isError || !detailQ.data) {
    return (
      <p className="mx-auto w-full text-sm text-rust-600">
        Could not load this product.{" "}
        <Link
          to="/admin/catalog/products"
          className="underline hover:text-brown-500"
        >
          Back to products
        </Link>
        .
      </p>
    );
  }

  const product = detailQ.data.product;
  const categories = categoriesQ.data?.rows ?? [];

  const onSaved = (): void => {
    void queryClient.invalidateQueries({ queryKey: detailKey });
  };

  return (
    <div className="mx-auto w-full space-y-10">
      {/* Back link */}
      <Link
        to={backTo}
        className="inline-flex items-center gap-1 text-sm font-semibold text-brown-300 hover:text-brown-500"
      >
        <ArrowLeft className="h-4 w-4" /> Back to results
      </Link>

      <HeaderSection product={product} onSaved={onSaved} custom={detailQ.data.custom === true} />

      <div className="grid gap-10 lg:grid-cols-[2fr_3fr]">
        <ImagesSection product={product} onSaved={onSaved} />
        <DetailsForm
          product={product}
          categories={categories}
          onSaved={onSaved}
        />
      </div>

      {/* Prices by quantity / capacity — admins only (Jake 2026-11-02). */}
      {isAdmin && <PriceEditorSection merchdProductId={product.id} onSaved={onSaved} />}

      <OffersSection offers={product.offers} />
    </div>
  );
}

// ─── Header ────────────────────────────────────────────────────────────

function HeaderSection(props: {
  product: AdminProductDetail;
  onSaved: () => void;
  custom: boolean;
}) {
  const { product, onSaved } = props;
  const [error, setError] = useState<string | null>(null);

  const toggleVisibility = useMutation({
    mutationFn: async () =>
      api<{ product: AdminProductDetail }>(
        `/admin/catalog/products/${product.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({ is_active: !product.isActive }),
        },
      ),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      onSaved();
    },
  });

  const decorated = product.lowestDecoratedPiecePrice;
  const blank = product.lowestBlankPiecePrice;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            Admin · Catalog
          </p>
          <h1 className="mt-1 text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
            {product.name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-brown-400">
            {product.sku && <span className="font-semibold text-ink">{product.sku}</span>}
            {product.sku && (product.brand || product.category) && <span>·</span>}
            {product.brand && <span>{product.brand}</span>}
            {product.brand && product.category && <span>·</span>}
            {product.category ? (
              <span>{product.category.name}</span>
            ) : (
              <span className="text-rust-500">Uncategorized</span>
            )}
            <span>·</span>
            <StatusPill tone={product.isActive ? "active" : "warn"}>
              {product.isActive ? "Live" : "Hidden"}
            </StatusPill>
          </div>
        </div>
        {props.custom && (
          <DeleteEntityAction
            deletePath={`/catalog/manual-products/${product.id}`}
            triggerLabel="Delete product"
            allowedRoles={["admin", "production_rep", "client_rep"]}
            confirmTitle="Delete this custom product?"
            confirmBody={
              <>
                This removes <strong className="font-semibold text-brown-500">{product.name}</strong> from the catalogue
                and product search. Quotes and presentations that already use it keep their copy.
              </>
            }
            redirectTo="/admin/catalog/products"
            flashMessage={`${product.name} deleted.`}
            errorFallback="We couldn't delete the product. Try again."
          />
        )}
        <Button
          variant={product.isActive ? "outline" : "primary"}
          size="md"
          onClick={() => toggleVisibility.mutate()}
          disabled={toggleVisibility.isPending}
        >
          {toggleVisibility.isPending
            ? "Saving…"
            : product.isActive
              ? "Hide from storefront"
              : "Show on storefront"}
        </Button>
      </div>
      {error && <p className="text-sm text-rust-600">{error}</p>}
      <div className="grid grid-cols-2 gap-4 rounded-lg border border-brown-50 bg-white px-5 py-4 md:grid-cols-4">
        <PriceCell label="Decorated" value={decorated} highlight />
        <PriceCell label="Blank" value={blank} />
        <Cell
          label="Offers"
          value={product.offers.length.toLocaleString()}
        />
        <Cell
          label="Variants"
          value={product.offers
            .reduce((sum, o) => sum + o.variantCount, 0)
            .toLocaleString()}
        />
      </div>
    </div>
  );
}

function PriceCell(props: {
  label: string;
  value: string | null;
  highlight?: boolean;
}) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-brown-300">
        {props.label}
      </p>
      <p
        className={cn(
          "mt-1 font-display text-xl font-bold",
          props.highlight ? "text-brown-500" : "text-brown-400",
        )}
      >
        {props.value ? `$${Number(props.value).toFixed(2)}` : "—"}
      </p>
    </div>
  );
}

function Cell(props: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-brown-300">
        {props.label}
      </p>
      <p className="mt-1 font-display text-xl font-bold text-brown-500">
        {props.value}
      </p>
    </div>
  );
}

// ─── Images ────────────────────────────────────────────────────────────

function ImagesSection(props: {
  product: AdminProductDetail;
  onSaved: () => void;
}) {
  const { product, onSaved } = props;
  const usingFallback = product.imageUrls.length === 0;
  const displayed =
    product.imageUrls.length > 0 ? product.imageUrls : product.fallbackImageUrls;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(product.imageUrls.join("\n"));
  const [error, setError] = useState<string | null>(null);

  // Reset draft when the source changes (e.g. successful save returns
  // new state, or user navigates between products).
  useEffect(() => {
    setDraft(product.imageUrls.join("\n"));
  }, [product.imageUrls]);

  const save = useMutation({
    mutationFn: async (urls: string[]) =>
      api<{ product: AdminProductDetail }>(
        `/admin/catalog/products/${product.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({ image_urls: urls }),
        },
      ),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      setEditing(false);
      onSaved();
    },
  });

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between">
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Images
        </p>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs font-semibold text-brown-400 underline hover:text-brown-500"
          >
            Edit
          </button>
        )}
      </div>
      {usingFallback && !editing && (
        <p className="rounded-md border border-cream-300 bg-cream-100 px-3 py-2 text-xs text-brown-400">
          Showing the preferred supplier offer's images. Edit to override
          with curated URLs.
        </p>
      )}
      {displayed.length === 0 && !editing && (
        <div className="flex aspect-square w-full items-center justify-center rounded-lg border border-brown-100 bg-cream-100 text-sm text-brown-300">
          No image
        </div>
      )}
      {displayed.length > 0 && !editing && (
        <div className="space-y-2">
          <div className="aspect-square w-full overflow-hidden rounded-lg border border-brown-100 bg-cream-100">
            <img
              src={displayed[0]}
              alt=""
              className="h-full w-full object-cover"
            />
          </div>
          {displayed.length > 1 && (
            <div className="grid grid-cols-4 gap-2">
              {displayed.slice(1, 9).map((url, idx) => (
                <div
                  key={`${url}-${idx}`}
                  className="aspect-square overflow-hidden rounded-md border border-brown-100 bg-cream-100"
                >
                  <img
                    src={url}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {editing && (
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={6}
            placeholder="One URL per line. Leave empty to fall back to the preferred supplier offer's images."
            className="w-full rounded-md border border-brown-100 bg-white px-3 py-2 font-mono text-xs text-brown-500 placeholder:text-[#8a8a85] focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200"
          />
          {error && <p className="text-sm text-rust-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setError(null);
                setEditing(false);
                setDraft(product.imageUrls.join("\n"));
              }}
              disabled={save.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={save.isPending}
              onClick={() => {
                const urls = draft
                  .split(/\r?\n/)
                  .map((s) => s.trim())
                  .filter((s) => s.length > 0);
                save.mutate(urls);
              }}
            >
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

// ─── Details form (name, brand, description, category) ────────────────

function DetailsForm(props: {
  product: AdminProductDetail;
  categories: AdminCategoryRow[];
  onSaved: () => void;
}) {
  const { product, categories, onSaved } = props;
  const [name, setName] = useState(product.name);
  const [brand, setBrand] = useState(product.brand ?? "");
  const [description, setDescription] = useState(product.description ?? "");
  const [categoryId, setCategoryId] = useState(product.category?.id ?? "");
  const [costOverride, setCostOverride] = useState(
    product.costOverrideDecorated ?? "",
  );
  const [error, setError] = useState<string | null>(null);

  // Sync local state to the latest fetched product when the underlying
  // data changes (e.g., another field saved successfully).
  useEffect(() => {
    setName(product.name);
    setBrand(product.brand ?? "");
    setDescription(product.description ?? "");
    setCategoryId(product.category?.id ?? "");
    setCostOverride(product.costOverrideDecorated ?? "");
  }, [
    product.id,
    product.name,
    product.brand,
    product.description,
    product.category?.id,
    product.costOverrideDecorated,
  ]);

  const save = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      api<{ product: AdminProductDetail }>(
        `/admin/catalog/products/${product.id}`,
        {
          method: "PATCH",
          body: JSON.stringify(body),
        },
      ),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      onSaved();
    },
  });

  const sortedCategories = useMemo(
    () =>
      [...categories]
        .filter((c) => c.isActive)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [categories],
  );

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    setError(null);
    const body: Record<string, unknown> = {};
    if (name.trim() !== product.name) body.name = name.trim();
    const brandTrim = brand.trim();
    const currBrand = product.brand ?? "";
    if (brandTrim !== currBrand) body.brand = brandTrim.length === 0 ? null : brandTrim;
    const descTrim = description.trim();
    const currDesc = product.description ?? "";
    if (descTrim !== currDesc) {
      body.description = descTrim.length === 0 ? null : descTrim;
    }
    const currCatId = product.category?.id ?? "";
    if (categoryId !== currCatId) {
      body.merchd_category_id = categoryId === "" ? null : categoryId;
    }
    const costTrim = costOverride.trim();
    const currCost = product.costOverrideDecorated ?? "";
    if (costTrim !== currCost) {
      if (costTrim === "") {
        body.cost_override_decorated = null;
      } else {
        const n = Number(costTrim);
        if (!Number.isFinite(n) || n <= 0) {
          setError(
            "EQP cost override must be a positive number (or blank to clear).",
          );
          return;
        }
        body.cost_override_decorated = costTrim;
      }
    }
    if (Object.keys(body).length === 0) {
      setError("Nothing to save.");
      return;
    }
    save.mutate(body);
  };

  const dirty =
    name.trim() !== product.name ||
    brand.trim() !== (product.brand ?? "") ||
    description.trim() !== (product.description ?? "") ||
    categoryId !== (product.category?.id ?? "") ||
    costOverride.trim() !== (product.costOverrideDecorated ?? "");

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
        Details
      </p>

      <Field label="Name">
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={400} />
      </Field>

      <Field label="Brand">
        <Input value={brand} onChange={(e) => setBrand(e.target.value)} maxLength={200} />
      </Field>

      <Field label="Category">
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className="h-10 w-full rounded-md border border-brown-100 bg-white px-3 text-sm text-brown-500 focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200"
        >
          <option value="">Uncategorized</option>
          {sortedCategories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Description">
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={8}
          maxLength={20_000}
          className="w-full rounded-md border border-brown-100 bg-white px-3 py-2 text-sm text-brown-500 placeholder:text-[#8a8a85] focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200"
          placeholder="Marketing description shown on the storefront and the client portal."
        />
      </Field>

      <Field label="EQP cost override (decorated, $/unit)">
        <Input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={costOverride}
          onChange={(e) => setCostOverride(e.target.value)}
          placeholder="Leave blank to use the supplier's price"
        />
        {/* Plain words, no supplier name (Stephanie 2026-11-02: "stop using the word
            ingest … why does this say PCNA if its a different vendor"). */}
        <p className="mt-1 text-xs leading-snug text-brown-300">
          Our EQP cost per decorated unit, if this supplier gives us one. When
          set, presentations use it instead of the supplier's listed price, at
          every quantity. Updating the supplier's prices won't change it.
        </p>
      </Field>

      {error && <p className="text-sm text-rust-600">{error}</p>}

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={!dirty || save.isPending}
          onClick={() => {
            setName(product.name);
            setBrand(product.brand ?? "");
            setDescription(product.description ?? "");
            setCategoryId(product.category?.id ?? "");
            setCostOverride(product.costOverrideDecorated ?? "");
            setError(null);
          }}
        >
          Reset
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!dirty || save.isPending}
        >
          {save.isPending ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </form>
  );
}

function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
        {props.label}
      </span>
      {props.children}
    </label>
  );
}

// ─── Supplier offers ──────────────────────────────────────────────────

function OffersSection(props: { offers: AdminProductOfferDetail[] }) {
  if (props.offers.length === 0) {
    return (
      <section>
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Supplier offers
        </p>
        <p className="mt-3 text-sm text-brown-300">No active offers.</p>
      </section>
    );
  }
  return (
    <section className="space-y-3">
      <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
        Supplier offers
      </p>
      <div className="space-y-2">
        {props.offers.map((offer) => (
          <OfferRow key={offer.offerId} offer={offer} />
        ))}
      </div>
    </section>
  );
}

function OfferRow({ offer }: { offer: AdminProductOfferDetail }) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-white px-4 py-3",
        offer.isPreferred ? "border-peach-400" : "border-brown-100",
      )}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-display text-base font-bold text-brown-500">
          {offer.vendorName}
        </span>
        {offer.vendorSlug && (
          <span className="text-[10px] font-semibold uppercase tracking-wide text-brown-300">
            {offer.vendorSlug}
          </span>
        )}
        {offer.isPreferred && (
          <StatusPill tone="active">Preferred</StatusPill>
        )}
        {!offer.isActive && <StatusPill tone="warn">Inactive</StatusPill>}
        <span className="ml-auto text-xs text-brown-400">
          {offer.variantCount.toLocaleString()} variant
          {offer.variantCount === 1 ? "" : "s"}
        </span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-3 text-xs md:grid-cols-3">
        <OfferPrice label="Blank" value={offer.lowestBlankPiecePrice} />
        <OfferPrice
          label="Decorated"
          value={offer.lowestDecoratedPiecePrice}
        />
        <span className="text-brown-300">
          {offer.imageUrls.length.toLocaleString()} image
          {offer.imageUrls.length === 1 ? "" : "s"}
        </span>
      </div>
    </div>
  );
}

function OfferPrice(props: { label: string; value: string | null }) {
  return (
    <span className="text-brown-400">
      {props.label}:{" "}
      {props.value ? (
        <span className="font-semibold text-brown-500">
          ${Number(props.value).toFixed(2)}
        </span>
      ) : (
        <span className="text-rust-500">—</span>
      )}
    </span>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────

function extractError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.message ?? body?.error ?? `Request failed (${err.status}).`;
  }
  return "Save failed. Try again or check your connection.";
}
